import React from 'react'
import useSites, { groupSites } from '../../multiLocation/useSites'
import { sitesService, siteColour } from '../../../_services/sites.service'

// Multi-location sends for the marketing-v2 composers and Bailey's plans.
//
// A group campaign is N per-site sends (decision K), never one send over a
// merged audience: the campaign goes out here as normal, then the server
// copies it to each chosen location and sends or schedules it THERE, under
// that location's clients, consent, credits and business details
// (POST /sites/campaigns/broadcast with a schedule). A client who visits
// several locations gets it once. The server is idempotent per location, so
// a retry hands back the copy it already made instead of sending twice.
//
// Owner of a group with at least one other location only. Everyone else —
// every single-site salon — gets nothing rendered and nothing called.

/** Locations this person may also send from: the group's other sites where they're the owner and have a record (the server skips any other). */
export const otherSendSites = (data) => {
  const { sites = [], group, active } = data || {}
  if (!active || active.role !== 'owner') return []
  return groupSites({ sites, group })
    .filter(s => String(s.salonId) !== String(active.salonId) && s.role === 'owner' && s.hasRecord)
}

export const useSendSites = () => {
  const s = useSites()
  const others = s.inGroup ? otherSendSites({ sites: s.sites, group: s.group, active: s.active }) : []
  return { show: others.length > 0, active: s.active, others }
}

/** After the send here succeeded: the same campaign at `salonIds`, sent now or scheduled like this one. Resolves to the server's per-site list. */
export const broadcastToSites = async (campaignId, salonIds, { sendNow }) => {
  if (!campaignId || !salonIds || !salonIds.length) return []
  const res = await sitesService.broadcastCampaign(campaignId, salonIds, { sendNow: !!sendNow })
  return (res && res.created) || []
}

/** One site's outcome, in words: { tone: 'ok' | 'warn' | 'muted', label, detail }. */
export const describeSiteResult = (r) => {
  if (!r) return { tone: 'muted', label: 'Not sent', detail: '' }
  if (r.skipped) return { tone: 'muted', label: 'Skipped', detail: 'Your account isn’t attached to this location.' }
  const already = r.existing ? ' (it was already there)' : ''
  if (r.status === 'sending' || r.status === 'sent') return { tone: 'ok', label: 'Sent', detail: `To this location’s own clients${already}.` }
  if (r.status === 'scheduled') return { tone: 'ok', label: 'Scheduled', detail: `For this location’s own clients${already}.` }
  if (r.status === 'skipped') return { tone: 'muted', label: 'Called off', detail: 'Those times had already filled there.' }
  // Our own words for a low balance: the server's reason may still say "credits".
  if (r.reasonCode === 'insufficient_credits' || r.reasonCode === 'insufficient_balance') return { tone: 'warn', label: 'Left as a draft', detail: 'That location’s message balance is too low. Top up there, then send it.' }
  return { tone: 'warn', label: 'Left as a draft', detail: r.reason || 'It’s saved there as a draft for you to send.' }
}

// Each composer's own card look, so the card sits in its review step as a native.
const CARD = {
  email: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e5e5', padding: '24px', marginBottom: '20px' },
  sms: { backgroundColor: '#fff', borderRadius: 14, border: '1px solid #ECECF0', padding: 20, marginBottom: 16 },
}
const HEAD = {
  email: { fontSize: '12px', fontWeight: '600', color: '#9ca3af', margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.5px' },
  sms: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8E8E93', margin: '0 0 8px' },
}
const chip = (on, locked) => ({
  display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 13px', borderRadius: 999,
  border: `1px solid ${on ? '#29282D' : '#E5E5EA'}`, background: on ? '#29282D' : '#fff', color: on ? '#fff' : '#4B4B52',
  fontSize: 13, fontWeight: 600, cursor: locked ? 'default' : 'pointer', fontFamily: 'inherit', lineHeight: 1.2
})
const dot = (id) => ({ width: 8, height: 8, borderRadius: '50%', background: siteColour(id), flexShrink: 0 })

/**
 * "Locations" card for the review step. `value` = the other salonIds chosen
 * (default []: only here). Renders nothing unless this is a group owner.
 */
export const SendLocationsCard = ({ value = [], onChange, channel = 'email', disabled }) => {
  const { show, active, others } = useSendSites()
  if (!show) return null
  const chosen = new Set((value || []).map(String))
  const toggle = (id) => {
    const next = new Set(chosen)
    if (next.has(id)) next.delete(id); else next.add(id)
    onChange(others.map(s => String(s.salonId)).filter(x => next.has(x)))
  }
  const noun = channel === 'sms' ? 'text' : 'email'
  const look = channel === 'sms' ? 'sms' : 'email'
  return (
    <div style={CARD[look]}>
      <h3 style={HEAD[look]}>Locations</h3>
      <p style={{ fontSize: '13px', color: '#6b7280', margin: '0 0 14px', lineHeight: 1.5 }}>
        Also send this {noun} from your other locations. Each one sends to its own clients, with its own consent and message balance. Someone who visits more than one location gets it once.
      </p>
      <div role="group" aria-label="Send from" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {active && (
          <span style={chip(true, true)} title="The location you’re working in">
            <span aria-hidden style={dot(active.salonId)} />{active.salonName}
            <span style={{ fontSize: 10, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.04em' }}>here</span>
          </span>
        )}
        {others.map((s) => {
          const id = String(s.salonId)
          const on = chosen.has(id)
          return (
            <button key={id} type="button" aria-pressed={on} disabled={disabled} onClick={() => toggle(id)} style={chip(on, false)}>
              <span aria-hidden style={{ width: 12, textAlign: 'center', fontWeight: 700 }}>{on ? '✓' : '+'}</span>
              <span aria-hidden style={dot(id)} />{s.salonName}
            </button>
          )
        })}
      </div>
      {chosen.size > 0 && (
        <p style={{ fontSize: '12px', color: '#8e8e93', margin: '12px 0 0', lineHeight: 1.5 }}>
          If a location can’t send it (its message balance is too low, or no business details), it’s saved there as a draft and we’ll tell you why.
        </p>
      )}
    </div>
  )
}

const TONE = { ok: '#1a7f37', warn: '#A05A1F', muted: '#8e8e93' }

/** Per-location outcome list (the sheet after a send, and inside Bailey's plan sheet). */
export const SiteResultsList = ({ results = [], sites = [] }) => {
  const nameOf = (id) => (sites.find(s => String(s.salonId) === String(id)) || {}).salonName || 'Another location'
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
      {results.map((r) => {
        const d = describeSiteResult(r)
        return (
          <li key={String(r.salonId)} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', border: '1px solid #ECECF0', borderRadius: 10, textAlign: 'left' }}>
            <span aria-hidden style={{ ...dot(r.salonId), marginTop: 6 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <b style={{ fontSize: 14, color: '#1A1A1E' }}>{nameOf(r.salonId)}</b>
                <span style={{ fontSize: 13, fontWeight: 600, color: TONE[d.tone] }}>{d.label}</span>
              </span>
              {d.detail && <span style={{ display: 'block', fontSize: 12.5, color: d.tone === 'warn' ? TONE.warn : '#6b7280', marginTop: 2, lineHeight: 1.45 }}>{d.detail}</span>}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Sheet shown after a send that also went to other locations. `error` when
 * the call to the other locations failed outright (the send here stands);
 * `onRetry` tries again — safe, the server hands back copies it already made.
 */
export const SendLocationsResults = ({ title, results, sites, error, retrying, onRetry, onDone }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 100012, background: 'rgba(26,26,30,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
    <div role="dialog" aria-modal="true" aria-label="Your other locations" style={{ width: '100%', maxWidth: '440px', maxHeight: '85vh', overflow: 'auto', background: '#fff', borderRadius: '16px', padding: '26px' }}>
      <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#1A1A1E', margin: '0 0 6px' }}>{title}</h2>
      <p style={{ fontSize: '14px', color: '#4B4B52', lineHeight: 1.6, margin: '0 0 16px' }}>Your other locations:</p>
      {error ? (
        <p role="alert" style={{ fontSize: 13.5, color: TONE.warn, lineHeight: 1.5, margin: '0 0 8px' }}>
          {error} Nothing was sent twice. Try again to finish the other locations.
        </p>
      ) : (
        <SiteResultsList results={results} sites={sites} />
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {error && onRetry && (
          <button type="button" onClick={onRetry} disabled={retrying} style={{ flex: 1, padding: '12px', border: '1px solid #ECECF0', borderRadius: '10px', background: '#fff', fontSize: '14px', fontWeight: 600, color: '#1A1A1E', cursor: 'pointer', fontFamily: 'inherit' }}>
            {retrying ? 'Trying…' : 'Try again'}
          </button>
        )}
        <button type="button" onClick={onDone} disabled={retrying} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '10px', background: '#29282D', fontSize: '14px', fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' }}>
          Done
        </button>
      </div>
    </div>
  </div>
)
